import { fetchPost, fetchPatch } from "../support/api-helpers";

class PaymentManager {
  constructor() {
    this.addModal = null;
    this.editModal = null;
    this.addForm = null;
    this.editForm = null;
    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.addModal = document.getElementById('add-payment-modal');
      this.editModal = document.getElementById('edit-payment-modal');
      this.addForm = document.getElementById('add-payment-form');
      this.editForm = document.getElementById('edit-payment-form');

      this.setupAddModalEventListeners();
      this.setupEditModalEventListeners();
      this.setDefaultDate();
    });
  }

  setupAddModalEventListeners() {
    if (!this.addModal || !this.addForm) return;

    const closeBtn = this.addModal.querySelector('.close');
    const cancelBtn = document.getElementById('cancel-add-payment');

    // Close modal handlers
    if (closeBtn) closeBtn.onclick = () => this.closeAddModal();
    if (cancelBtn) cancelBtn.onclick = () => this.closeAddModal();

    // Form submission
    this.addForm.onsubmit = (e) => this.handleAddSubmit(e);
  }

  setupEditModalEventListeners() {
    if (!this.editModal || !this.editForm) return;

    const closeBtn = this.editModal.querySelector('.close');
    const cancelBtn = document.getElementById('cancel-edit-payment');

    // Close modal handlers
    if (closeBtn) closeBtn.onclick = () => this.closeEditModal();
    if (cancelBtn) cancelBtn.onclick = () => this.closeEditModal();

    // Form submission
    this.editForm.onsubmit = (e) => this.handleEditSubmit(e);
  }

  setDefaultDate() {
    const paymentDateInput = document.getElementById('payment-date');
    if (paymentDateInput) {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      paymentDateInput.value = now.toISOString().slice(0, 16);
    }
  }

  openAddModal() {
    if (this.addModal) {
      this.addModal.style.display = 'block';
    }
  }

  closeAddModal() {
    if (this.addModal) {
      this.addModal.style.display = 'none';
    }
  }

  openEditModal(paymentData) {
    if (this.editModal) {
      // Populate the form with existing payment data
      document.getElementById('edit-payment-id').value = paymentData.id;
      document.getElementById('edit-payment-amount').value = paymentData.amount || '';
      document.getElementById('edit-payment-status').value = paymentData.payment_status || '';
      document.getElementById('edit-payment-notes').value = paymentData.notes || '';

      // Format the date for the datetime-local input
      if (paymentData.payment_date) {
        const date = new Date(paymentData.payment_date);
        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        document.getElementById('edit-payment-date').value = date.toISOString().slice(0, 16);
      }

      this.editModal.style.display = 'block';
    }
  }

  closeEditModal() {
    if (this.editModal) {
      this.editModal.style.display = 'none';
    }
  }

  handleAddSubmit(e) {
    e.preventDefault();

    const formData = {
      amount: document.getElementById('payment-amount').value,
      status: document.getElementById('payment-status').value,
      payment_date: document.getElementById('payment-date').value,
      external_source_type: document.getElementById('external-source-type').value,
      external_source_id: document.getElementById('external-source-id').value,
      notes: document.getElementById('payment-notes').value
    };

    // Get subscription details from the page URL
    const pathParts = window.location.pathname.split('/');
    const identityId = pathParts[3]; // /admin/user/{identity_id}/subscriptions/{subscription_id}
    const subscriptionId = pathParts[5];

    // Create the payment via API using api-helpers
    fetchPost({
      url: `/admin/identity/${identityId}/subscriptions/${subscriptionId}/payments`,
      data: formData
    })
    .then(data => {
      this.closeAddModal();
      window.location.reload();
    })
    .catch(error => {
      console.error('Error creating payment:', error);
      alert(`Error creating payment: ${error.message}`);
    });
  }

  handleEditSubmit(e) {
    e.preventDefault();

    const paymentId = document.getElementById('edit-payment-id').value;
    const formData = {
      amount: document.getElementById('edit-payment-amount').value,
      status: document.getElementById('edit-payment-status').value,
      payment_date: document.getElementById('edit-payment-date').value,
      notes: document.getElementById('edit-payment-notes').value
    };

    // Get subscription details from the page URL
    const pathParts = window.location.pathname.split('/');
    const identityId = pathParts[3];
    const subscriptionId = pathParts[5];

    // Update the payment via API using api-helpers
    fetchPatch({
      url: `/admin/identity/${identityId}/subscriptions/${subscriptionId}/payments/${paymentId}`,
      data: formData
    })
    .then(data => {
      this.closeEditModal();
      window.location.reload();
    })
    .catch(error => {
      console.error('Error updating payment:', error);
      alert(`Error updating payment: ${error.message}`);
    });
  }

  deletePayment(paymentId) {
    if (!confirm('Are you sure you want to delete this payment? This action cannot be undone.')) {
      return;
    }

    // Get subscription details from the page URL
    const pathParts = window.location.pathname.split('/');
    const identityId = pathParts[3];
    const subscriptionId = pathParts[5];

    // Delete the payment via API
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    fetch(`/admin/identity/${identityId}/subscriptions/${subscriptionId}/payments/${paymentId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken ? csrfToken.content : null,
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        window.location.reload();
      } else {
        alert(`Error deleting payment: ${data.errors ? data.errors.join(', ') : 'Unknown error'}`);
      }
    })
    .catch(error => {
      console.error('Error deleting payment:', error);
      alert(`Error deleting payment: ${error.message}`);
    });
  }

  // Method to fetch payment data and open edit modal
  editPayment(paymentId) {
    // Get subscription details from the page URL
    const pathParts = window.location.pathname.split('/');
    const identityId = pathParts[3];
    const subscriptionId = pathParts[5];

    // Fetch payment data from server
    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    fetch(`/admin/identity/${identityId}/subscriptions/${subscriptionId}/payments/${paymentId}`, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken ? csrfToken.content : null,
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch payment data');
      }
      return response.json();
    })
    .then(paymentData => {
      this.openEditModal(paymentData);
    })
    .catch(error => {
      console.error('Error fetching payment data:', error);
      alert(`Error loading payment data: ${error.message}`);
    });
  }
}

// Initialize the payment manager
const paymentManager = new PaymentManager();

// Global functions for backward compatibility
window.openAddPaymentModal = function() {
  paymentManager.openAddModal();
};

window.editPayment = function(paymentId) {
  paymentManager.editPayment(paymentId);
};

window.deletePayment = function(paymentId) {
  paymentManager.deletePayment(paymentId);
};

// Close modals when clicking outside
window.onclick = function(event) {
  if (event.target === paymentManager.addModal) {
    paymentManager.closeAddModal();
  }
  if (event.target === paymentManager.editModal) {
    paymentManager.closeEditModal();
  }
};

export default PaymentManager;
